<template>
  <div class="other-personels">
    <!-- Search Bar -->
    <div class="search-bar" v-if="personels.length > 0">
      <div class="search-container">
        <i class="fas fa-search search-icon"></i>
        <input
          v-model="searchQuery"
          type="text"
          :placeholder="$t('search_placeholder')"
          class="search-input"
          @input="currentPage = 1"
        />
      </div>
    </div>

    <!-- Content Area -->
    <div class="content-wrapper">
      <!-- No Results Message -->
      <div v-if="filteredPersonels.length === 0" class="no-results">
        <i class="fas fa-user-slash no-results-icon"></i>
        <h3>{{ $t('no_results_title') }}</h3>
        <p>{{ $t('no_results_message') }}</p>
      </div>

      <!-- Cards Grid -->
      <div v-else class="cards-grid">
        <div v-for="(person, index) in paginatedPersonels" :key="index" class="person-card">
          <div class="card-header">
            <img 
              :src="person.photo || '/assets/images/default-photo.png'" 
              :alt="person.name" 
              class="person-image"
              @error="handleImageError"
            />
            <div class="person-titles">
              <h3 class="person-name">{{ person.name }}</h3>
              <h4 class="person-title">{{ person.title }}</h4>
            </div>
          </div>

          <div class="card-body">
            <!-- Email -->
            <div class="contact-item">
              <i class="fas fa-envelope contact-icon"></i>
              <div class="contact-info">
                <a 
                  v-if="person.email" 
                  :href="`mailto:${person.email}`" 
                  class="contact-link"
                >
                  {{ person.email }}
                </a>
                <span v-else class="no-contact">No email address</span>
              </div>
            </div>

            <!-- Facebook -->
            <div class="contact-item">
              <i class="fab fa-facebook-f contact-icon facebook-icon"></i>
              <div class="contact-info">
                <a 
                  v-if="person.facebook" 
                  :href="person.facebook" 
                  target="_blank" rel="noopener noreferrer" 
                  class="contact-link"
                >
                  {{ formatSocialLink(person.facebook) }}
                </a>
                <span v-else class="no-contact">No Facebook account</span>
              </div>
            </div>

            <!-- Twitter -->
            <div class="contact-item">
              <i class="fab fa-twitter contact-icon twitter-icon"></i>
              <div class="contact-info">
                <a 
                  v-if="person.twitter" 
                  :href="person.twitter" 
                  target="_blank" rel="noopener noreferrer" 
                  class="contact-link"
                >
                  {{ formatSocialLink(person.twitter) }}
                </a>
                <span v-else class="no-contact">No Twitter account</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div class="pagination-wrapper" v-if="filteredPersonels.length > itemsPerPage">
      <div class="pagination-controls">
        <button 
          @click="prevPage" 
          :disabled="currentPage === 1"
          class="pagination-button"
        >
          <i class="fas fa-chevron-left"></i> {{ $t('previous') }}
        </button>
        
        <div class="page-indicator">
          {{ $t('page_indicator', { current: currentPage, total: totalPages }) }}
        </div>
        
        <button 
          @click="nextPage" 
          :disabled="currentPage === totalPages"
          class="pagination-button"
        >
          {{ $t('next') }} <i class="fas fa-chevron-right"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "OtherPersonels",
  props: {
    personels: {
      type: Array,
      required: true,
      default: () => []
    }
  },
  data() {
    return {
      searchQuery: "",
      currentPage: 1,
      itemsPerPage: 8
    };
  },
  computed: {
    filteredPersonels() {
      if (!this.searchQuery) return this.personels;
      
      const query = this.searchQuery.toLowerCase();
      return this.personels.filter(person => {
        return (
          (person.name && person.name.toLowerCase().includes(query)) ||
          (person.title && person.title.toLowerCase().includes(query)) ||
          (person.email && person.email.toLowerCase().includes(query)) ||
          (person.facebook && person.facebook.toLowerCase().includes(query)) ||
          (person.twitter && person.twitter.toLowerCase().includes(query))
        );
      });
    },
    paginatedPersonels() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredPersonels.slice(start, end);
    },
    totalPages() {
      return Math.ceil(this.filteredPersonels.length / this.itemsPerPage);
    }
  },
  methods: {
    nextPage() {
      if (this.currentPage < this.totalPages) this.currentPage++;
    },
    prevPage() {
      if (this.currentPage > 1) this.currentPage--;
    },
    handleImageError(event) {
      event.target.src = '/assets/images/default-photo.png';
    },
    formatSocialLink(url) {
      if (!url) return '';
      try {
        const parsed = new URL(url);
        return parsed.hostname.replace('www.', '') + (parsed.pathname !== '/' ? parsed.pathname : '');
      } catch {
        return url.length > 20 ? url.substring(0, 17) + '...' : url;
      }
    }
  },
  watch: {
    '$route.query'(newQuery) {
      if (newQuery.section) {
        this.selectedItem = newQuery.section;
      }
      if (newQuery.q) {
        this.searchQuery = newQuery.q;
        this.filteredPersonels();
      }
    }
  },
};
</script>

<style scoped>
.other-personels {
  padding: 2rem 1rem;
  max-width: 1400px;
  margin: 0 auto;
  min-height: 60vh;
  display: flex;
  flex-direction: column;
}

.search-bar {
  margin-bottom: 2rem;
  display: flex;
  justify-content: center;
}

.search-container {
  position: relative;
  width: 100%;
  max-width: 500px;
}

.search-icon {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #6b7280;
}

.search-input {
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 3rem;
  border: 1px solid #d1d5db;
  border-radius: 2rem;
  font-size: 1rem;
  transition: all 0.2s ease;
}

.search-input:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  outline: none;
}

.content-wrapper {
  flex: 1;
}

.no-results {
  text-align: center;
  padding: 3rem 1rem;
  color: #4b5563;
}

.no-results-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  color: #9ca3af;
}

.no-results h3 {
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
}

.no-results p {
  font-size: 1rem;
  max-width: 500px;
  margin: 0 auto;
}

.cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
  padding: 0.5rem;
}

.person-card {
  background: white;
  border-radius: 0.75rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
}

.person-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 10px 15px rgba(0, 0, 0, 0.1);
}

.card-header {
  padding: 1.5rem 1.5rem 1rem;
  text-align: center;
  border-bottom: 1px solid #f3f4f6;
}

.person-image {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  object-fit: cover;
  margin: 0 auto 1rem;
  border: 3px solid #f3f4f6;
}

.person-name {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.25rem;
}

.person-title {
  font-size: 0.9rem;
  color: #6b7280;
  font-weight: 500;
}

.card-body {
  padding: 1rem 1.5rem;
  flex: 1;
}

.contact-item {
  display: flex;
  align-items: center;
  margin-bottom: 0.75rem;
}

.contact-icon {
  width: 1.5rem;
  text-align: center;
  margin-right: 0.75rem;
  color: #6b7280;
  font-size: 0.9rem;
}

.facebook-icon {
  color: #1877f2;
}

.twitter-icon {
  color: #1da1f2;
}

.contact-info {
  flex: 1;
  min-width: 0;
}

.contact-link {
  color: #3b82f6;
  text-decoration: none;
  font-size: 0.9rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
  transition: color 0.2s ease;
}

.contact-link:hover {
  color: #2563eb;
  text-decoration: underline;
}

.no-contact {
  color: #9ca3af;
  font-size: 0.9rem;
  font-style: italic;
}

.pagination-wrapper {
  margin-top: 2rem;
  padding: 1rem 0;
}

.pagination-controls {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
}

.pagination-button {
  padding: 0.5rem 1rem;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
  font-size: 0.9rem;
}

.pagination-button:not(:disabled):hover {
  background: #f9fafb;
  border-color: #9ca3af;
}

.pagination-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-indicator {
  font-size: 0.9rem;
  color: #4b5563;
}

@media (max-width: 768px) {
  .cards-grid {
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 1rem;
  }
  
  .person-card {
    border-radius: 0.5rem;
  }
  
  .card-header {
    padding: 1rem;
  }
  
  .person-image {
    width: 80px;
    height: 80px;
  }
  
  .person-name {
    font-size: 1.1rem;
  }
  
  .person-title {
    font-size: 0.8rem;
  }
  
  .contact-link, .no-contact {
    font-size: 0.8rem;
  }
}
</style>